import React, { useState, useEffect } from 'react';

interface SkewedInputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date' | 'time';
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  placeholder?: string;
  className?: string;
  variance?: number;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  as?: 'input' | 'textarea' | 'select';
  children?: React.ReactNode; // For select options
  rows?: number; // For textarea
  min?: number;
  max?: number;
  enableRandomSkew?: boolean; // Whether to apply random skew to borders
}

interface Point {
  x: number;
  y: number;
}

/**
 * An input/textarea/select component with randomly skewed quadrilateral borders using SVG.
 */
const SkewedInput: React.FC<SkewedInputProps> = ({
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  className = '',
  variance = 0.06,
  required = false,
  disabled = false,
  name,
  id,
  as = 'input',
  children,
  rows = 4,
  min,
  max,
  enableRandomSkew = true
}) => {
  const [points, setPoints] = useState<Point[]>([]);

  useEffect(() => {
    // Generate random offset within variance range
    const getRandomOffset = () => {
      return enableRandomSkew ? (Math.random() - 0.5) * variance * 100 : 0;
    };

    // Generate four corners with slight random variations
    const corners = [
      { x: 0 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-left
      { x: 100 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-right
      { x: 100 + getRandomOffset(), y: 100 + getRandomOffset() }, // bottom-right
      { x: 0 + getRandomOffset(), y: 100 + getRandomOffset() } // bottom-left
    ];

    setPoints(corners);
  }, [variance, enableRandomSkew]);

  // Convert percentage points to SVG polygon points string
  const svgPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  const baseStyles = 'relative w-full px-4 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-50';
  const combinedStyles = `${baseStyles} ${className}`;

  const commonProps = {
    value,
    onChange,
    onBlur,
    placeholder,
    required,
    disabled,
    name,
    id,
    className: combinedStyles
  };

  return (
    <div className="relative">
      {/* SVG border overlay */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <polygon
          points={svgPoints}
          fill="white"
          stroke="black"
          strokeWidth={0.4}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Input element */}
      {as === 'textarea' ? (
        <textarea
          {...commonProps}
          rows={rows}
          className={`${combinedStyles} resize-none`}
        />
      ) : as === 'select' ? (
        <select {...commonProps}>
          {children}
        </select>
      ) : (
        <input
          {...commonProps}
          type={type}
          min={min}
          max={max}
        />
      )}
    </div>
  );
};

export default SkewedInput;
